import React, { useState, useEffect } from 'react';
import { Modal, Table, Spinner, Alert, Button, Row, Col } from 'react-bootstrap';
import { api } from 'api';
import { formatPrice } from 'common/utils';
import './_drillDownModal.scss';

const DrillDownModal = ({ show, onHide, type, title, filters = {} }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    if (show && type) {
      loadDrillDownData();
    }
  }, [show, type, filters, page]);

  const loadDrillDownData = async () => {
    setLoading(true);
    setError(null);

    try {
      let response;
      const params = {
        ...filters,
        page,
        page_size: 20
      };

      // Remove empty values
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === undefined) {
          delete params[key];
        }
      });

      switch (type) {
        case 'users':
          response = await api.getUsers(params);
          break;
        case 'grounds':
          response = await api.getGrounds(params);
          break;
        case 'bookings':
          response = await api.getBookings(params);
          break;
        default:
          throw new Error('Invalid drill-down type');
      }

      if (page === 1) {
        setData(response?.results || response || []);
      } else {
        setData(prev => [...prev, ...(response?.results || response || [])]);
      }

      setHasMore(response?.next ? true : false);
    } catch (error) {
      console.error('Error loading drill-down data:', error);
      setError('Failed to load detailed data');
    } finally {
      setLoading(false);
    }
  };

  const loadMore = () => {
    setPage(prev => prev + 1);
  };

  const getColumns = () => {
    switch (type) {
      case 'users':
        return [
          { key: 'id', label: 'ID' },
          { key: 'full_name', label: 'Name' },
          { key: 'email', label: 'Email' },
          { key: 'role', label: 'Role' },
          { key: 'created', label: 'Joined' }
        ];
      case 'grounds':
        return [
          { key: 'id', label: 'ID' },
          { key: 'name', label: 'Name' },
          { key: 'location', label: 'Location' },
          { key: 'avg_rating', label: 'Rating' },
          { key: 'is_active', label: 'Status' }
        ];
      case 'bookings':
        return [
          { key: 'id', label: 'ID' },
          { key: 'reference_number', label: 'Reference' },
          { key: 'user', label: 'User' },
          { key: 'ground_field', label: 'Ground' },
          { key: 'date', label: 'Date' },
          { key: 'status', label: 'Status' },
          { key: 'price', label: 'Price' }
        ];
      default:
        return [];
    }
  };

  const formatValue = (value, key) => {
    if (value === null || value === undefined) return '-';
    
    switch (key) {
      case 'created':
        return new Date(value).toLocaleDateString();
      case 'date':
        return new Date(value).toLocaleDateString();
      case 'is_active':
        return value ? 'Active' : 'Inactive';
      case 'role':
        return value?.charAt(0).toUpperCase() + value?.slice(1).replace('_', ' ');
      case 'status':
        return value?.charAt(0).toUpperCase() + value?.slice(1);
      case 'price':
        return `Rs. ${formatPrice(value)}`;
      case 'location':
        return value?.city || '-';
      case 'user':
        return value?.full_name || value?.email || '-';
      case 'ground_field':
        return value?.name || '-';
      default:
        return value;
    }
  };

  const columns = getColumns();

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
            <Button 
              variant="outline-danger" 
              size="sm" 
              className="ms-2" 
              onClick={loadDrillDownData}
            >
              Retry
            </Button>
          </Alert>
        )}

        {loading && data.length === 0 && (
          <div className="text-center py-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        )}

        {data.length > 0 && (
          <>
            <div className="drill-down-stats mb-3">
              <Row>
                <Col md={4}>
                  <div className="stat-item">
                    <span className="stat-label">Total Records:</span>
                    <span className="stat-value">{data.length}</span>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="stat-item">
                    <span className="stat-label">Page:</span>
                    <span className="stat-value">{page}</span>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="stat-item">
                    <span className="stat-label">Has More:</span>
                    <span className="stat-value">{hasMore ? 'Yes' : 'No'}</span>
                  </div>
                </Col>
              </Row>
            </div>

            <div className="table-responsive">
              <Table striped hover>
                <thead>
                  <tr>
                    {columns.map(column => (
                      <th key={column.key}>{column.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.map((item, index) => (
                    <tr key={item.id || index}>
                      {columns.map(column => (
                        <td key={column.key}>
                          {formatValue(item[column.key], column.key)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {hasMore && (
              <div className="text-center mt-3">
                <Button 
                  variant="outline-primary" 
                  onClick={loadMore}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Loading...
                    </>
                  ) : (
                    'Load More'
                  )}
                </Button>
              </div>
            )}
          </>
        )}

        {!loading && data.length === 0 && !error && (
          <div className="text-center py-4">
            <p className="text-muted">No data available</p>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DrillDownModal;
